import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import { useSelector } from "react-redux";
import Confetti from "react-confetti";
import { miniGameService } from "../../services/miniGameService";

const Game = () => {
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [discountCode, setDiscountCode] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [gameSettings, setGameSettings] = useState(null);
  const [isSpinReady, setIsSpinReady] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isBeforeStart: true,
    isEnded: false,
  });
  const { userInfo } = useSelector((state) => state.user);

  useEffect(() => {
    fetchGameSettings();
  }, []);

  useEffect(() => {
    if (gameSettings) {
      const now = new Date();
      const startDate = new Date(gameSettings.startDate);
      const endDate = new Date(gameSettings.endDate);

      if (now >= startDate && now <= endDate) {
        startQuiz();
      } else {
        setLoading(false);
      }

      const interval = setInterval(() => {
        updateCountdown();
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gameSettings]);

  const fetchGameSettings = async () => {
    try {
      const response = await miniGameService.fetchGameSettings();
      setGameSettings(response.data);
    } catch (err) {
      const errorMessage =
        err.response?.data || "Không thể tải cài đặt trò chơi.";
      setError(errorMessage);
      toast.error(errorMessage, { icon: "❌" });
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    setLoading(true);
    try {
      const response = await miniGameService.startQuiz(userInfo.id);
      setSessionId(response.data.sessionId);
      setQuestions(response.data.questions);
      setCurrentQuestionIndex(0);
      setAnswers([]);
      setResult(null);
      setError("");
      setIsSpinReady(false);
      setDiscountCode(null);
      toast.success("Trò chơi đố vui đã sẵn sàng!", { icon: "🎉" });
    } catch (err) {
      const errorMessage =
        err.response?.data || "Trò chơi hiện không khả dụng.";
      setError(errorMessage);
      toast.info(errorMessage, { icon: "❌" });
    } finally {
      setLoading(false);
    }
  };

  const updateCountdown = () => {
    if (!gameSettings) return;

    const now = new Date();
    const startDate = new Date(gameSettings.startDate);
    const endDate = new Date(gameSettings.endDate);

    let targetDate = now < startDate ? startDate : endDate;
    let isBeforeStart = now < startDate;
    let isEnded = now > endDate;

    const diff = targetDate - now;
    if (diff <= 0) {
      setCountdown({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isBeforeStart,
        isEnded,
      });
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    setCountdown({
      days,
      hours,
      minutes,
      seconds,
      isBeforeStart,
      isEnded,
    });
  };

  const submitAnswer = async () => {
    const newAnswers = [
      ...answers,
      {
        questionId: questions[currentQuestionIndex].id,
        answer: selectedAnswer,
      },
    ];
    setAnswers(newAnswers);
    setSelectedAnswer("");

    if (currentQuestionIndex + 1 < questions.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      try {
        const response = await miniGameService.submitAnswers(
          sessionId,
          newAnswers,
          userInfo.id
        );
        setResult(response.data);
        if (response.data.allCorrect) {
          setIsSpinReady(true); // Cho phép quay trong cả hai trường hợp
          toast.success(
            "Chúc mừng! Bạn đã trả lời đúng tất cả câu hỏi! Hãy quay vòng quay may mắn!",
            {
              icon: "🎉",
            }
          );
        } else {
          toast.info("Một số câu trả lời không đúng!", { icon: "😔" });
        }
      } catch (err) {
        setError("Lỗi khi gửi câu trả lời.");
        toast.error("Lỗi khi gửi câu trả lời.", { icon: "❌" });
      }
    }
  };

  const handleSpin = () => {
    setSpinning(true);
    setIsSpinReady(false); // Vô hiệu hóa nút quay
    setTimeout(() => {
      setSpinning(false);
      if (result.isWinner && result.discountCode) {
        setDiscountCode(result.discountCode);
        toast.success("Chúc mừng! Bạn đã nhận được mã giảm giá!", {
          icon: "🎉",
        });
      } else {
        toast.info("Rất tiếc, lần này bạn chưa nhận được mã giảm giá!", {
          icon: "ℹ️",
        });
      }
    }, 3000);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-b from-blue-50 to-white">
        <ClipLoader size={50} color="#2563eb" />
        <p className="mt-4 text-lg text-gray-600">Đang tải trò chơi...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <motion.div
          className="bg-red-100 p-6 rounded-lg shadow-lg text-center max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-lg text-gray-700 font-semibold">
            Thời gian còn lại để tham gia: {countdown.days} ngày,{" "}
            {countdown.hours} giờ, {countdown.minutes} phút, {countdown.seconds}{" "}
            giây
          </p>
          <p className="text-xl text-red-600">{error}</p>
          {/* <button
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            onClick={fetchGameSettings}
            aria-label="Thử lại trò chơi"
          >
            Thử lại
          </button> */}
        </motion.div>
      </div>
    );
  }

  if (
    gameSettings &&
    countdown.isBeforeStart &&
    countdown.days + countdown.hours + countdown.minutes + countdown.seconds > 0
  ) {
    return (
      <motion.div
        className="container mx-auto p-6 text-center bg-gradient-to-b from-blue-50 to-white min-h-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-4xl font-bold text-blue-600 mb-6">
          Sẵn sàng cho trò chơi đố vui du lịch Việt Nam!
        </h2>
        <p className="text-lg text-gray-700 mb-4">Trò chơi sẽ bắt đầu sau:</p>
        <div className="flex justify-center space-x-4 mb-6">
          {["days", "hours", "minutes", "seconds"].map((unit, idx) => (
            <motion.div
              key={unit}
              className="bg-blue-100 p-4 rounded-lg shadow-md"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: idx * 0.1, duration: 0.3 }}
            >
              <p className="text-3xl font-bold text-blue-600">
                {countdown[unit]}
              </p>
              <p className="text-sm text-gray-600">
                {unit === "days"
                  ? "Ngày"
                  : unit === "hours"
                  ? "Giờ"
                  : unit === "minutes"
                  ? "Phút"
                  : "Giây"}
              </p>
            </motion.div>
          ))}
        </div>
        <p className="text-lg text-gray-700 mb-6 max-w-xl mx-auto">
          Hãy chuẩn bị khám phá những điểm đến tuyệt vời và nhận mã giảm giá hấp
          dẫn khi tham gia trò chơi đố vui của chúng tôi!
        </p>
        <img
          src="https://scontent.fdad4-1.fna.fbcdn.net/v/t39.30808-6/505371194_122116665206876808_8068198464818928353_n.jpg?_nc_cat=111&ccb=1-7&_nc_sid=127cfc&_nc_ohc=hwk_lW0ea6YQ7kNvwE-zaX5&_nc_oc=AdlWYZ0kzi2e99apF6B_ufe86v9VkdVGXpP73RArarWrBWU94SWubwnsFp4jf3JxiBM&_nc_zt=23&_nc_ht=scontent.fdad4-1.fna&_nc_gid=VZFTbKoNzJOKZlZYEdqLSw&oh=00_AfOyuFelIlGn6cHWTurlv6-AzWMPmmqOZ7HEff__F6roAg&oe=685F176F"
          alt="Vietnam Travel"
          className="mx-auto rounded-lg shadow-lg mb-6 max-w-2xl w-full"
        />
      </motion.div>
    );
  }

  if (gameSettings && countdown.isEnded) {
    return (
      <motion.div
        className="container mx-auto p-6 text-center bg-gradient-to-b from-blue-50 to-white min-h-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-4xl font-bold text-red-600 mb-6">
          Trò chơi đã kết thúc!
        </h2>
        <p className="text-lg text-gray-700 mb-6 max-w-xl mx-auto">
          Cảm ơn bạn đã tham gia. Hãy theo dõi để biết thêm các trò chơi thú vị
          trong tương lai!
        </p>
        <motion.button
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          onClick={fetchGameSettings}
          aria-label="Kiểm tra lại trạng thái trò chơi"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Kiểm tra lại
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="container mx-auto p-4 bg-gradient-to-b from-blue-50 to-white min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {result && result.isWinner && discountCode && <Confetti />}
      {gameSettings && (
        <motion.div
          className="text-center mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <p className="text-lg text-gray-700 font-semibold">
            Thời gian còn lại để tham gia: {countdown.days} ngày,{" "}
            {countdown.hours} giờ, {countdown.minutes} phút, {countdown.seconds}{" "}
            giây
          </p>
        </motion.div>
      )}
      {questions.length > 0 && !result && (
        <motion.div
          className="bg-white p-6 rounded-lg shadow-lg max-w-2xl mx-auto"
          key={currentQuestionIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              Câu hỏi {currentQuestionIndex + 1} / {questions.length}
            </h2>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{
                  width: `${
                    ((currentQuestionIndex + 1) / questions.length) * 100
                  }%`,
                }}
              ></div>
            </div>
          </div>
          <h3 className="text-xl mb-4">
            {questions[currentQuestionIndex].questionText}
          </h3>
          <div className="space-y-3">
            {["optionA", "optionB", "optionC", "optionD"].map((opt, idx) => (
              <motion.button
                key={opt}
                className={`w-full p-3 rounded-lg text-left ${
                  selectedAnswer === String.fromCharCode(65 + idx)
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                } transition-colors duration-200`}
                onClick={() => setSelectedAnswer(String.fromCharCode(65 + idx))}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label={`Chọn đáp án ${String.fromCharCode(65 + idx)}`}
              >
                {String.fromCharCode(65 + idx)}.{" "}
                {questions[currentQuestionIndex][opt]}
              </motion.button>
            ))}
          </div>
          <motion.button
            className="mt-6 bg-green-500 text-white px-6 py-2 rounded-lg disabled:bg-gray-400 hover:bg-green-600 transition-colors duration-200"
            onClick={submitAnswer}
            disabled={!selectedAnswer}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label={
              currentQuestionIndex + 1 === questions.length
                ? "Hoàn thành bài kiểm tra"
                : "Câu hỏi tiếp theo"
            }
          >
            {currentQuestionIndex + 1 === questions.length
              ? "Hoàn thành bài kiểm tra"
              : "Câu hỏi tiếp theo"}
          </motion.button>
        </motion.div>
      )}

      {result && result.allCorrect && (
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl font-bold text-green-600 mb-4">
            Tất cả câu trả lời đều đúng! Hãy quay vòng quay may mắn!
          </h2>
          <div className="relative w-64 h-64 mx-auto">
            <motion.div
              className="w-full h-full rounded-full bg-gradient-to-r from-red-500 to-yellow-500 flex items-center justify-center"
              animate={{ rotate: spinning ? 360 : 0 }}
              transition={{
                duration: 3,
                ease: "easeOut",
                repeat: spinning ? Infinity : 0,
              }}
            >
              <div className="absolute text-white font-bold text-lg">
                Vòng quay may mắn
              </div>
            </motion.div>
          </div>
          {isSpinReady && (
            <motion.button
              className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              onClick={handleSpin}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Quay vòng quay may mắn"
            >
              Quay vòng quay may mắn
            </motion.button>
          )}
          {discountCode && (
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-2xl text-gray-800">
                Mã giảm giá của bạn:{" "}
                <strong className="text-blue-600">{discountCode}</strong>
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Sử dụng mã này khi thanh toán để nhận ưu đãi!
              </p>
            </motion.div>
          )}
          {!spinning && !isSpinReady && !discountCode && (
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-xl text-blue-500 mb-4">
                Rất tiếc, lần này bạn chưa nhận được mã giảm giá!
              </p>
              <motion.button
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                onClick={startQuiz}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Thử lại trò chơi"
              >
                Thử lại
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      )}

      {result && !result.allCorrect && (
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xl text-red-500 mb-4">
            Một số câu trả lời không đúng! Thử lại sau nhé.
          </p>
          <motion.button
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            onClick={startQuiz}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Thử lại trò chơi"
          >
            Thử lại
          </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Game;
